import { useState, useEffect } from 'react';
import type { EmployeeFollowup, CreateEmployeeFollowupRequest } from '../types';

interface FollowupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateEmployeeFollowupRequest) => Promise<void>;
  idEmployee: string;
  employeeName: string;
  date: string;
  existingFollowup?: EmployeeFollowup | null;
}

const FOLLOWUP_TYPES = [
  { value: 'Konseling', label: 'Konseling', color: 'bg-blue-100 text-blue-800' },
  { value: 'Peringatan', label: 'Peringatan', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'Observasi', label: 'Observasi', color: 'bg-purple-100 text-purple-800' },
  { value: 'Tindakan Medis', label: 'Tindakan Medis', color: 'bg-red-100 text-red-800' },
  { value: 'Follow Up', label: 'Follow Up', color: 'bg-green-100 text-green-800' },
  { value: 'Lain Lain', label: 'Lain Lain', color: 'bg-gray-100 text-gray-800' },
];

export function FollowupModal({
  isOpen,
  onClose,
  onSave,
  idEmployee,
  employeeName,
  date,
  existingFollowup,
}: FollowupModalProps) {
  const [followupType, setFollowupType] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (existingFollowup) {
      setFollowupType(existingFollowup.followup_type);
      setNotes(existingFollowup.notes || '');
    } else {
      setFollowupType('');
      setNotes('');
    }
  }, [existingFollowup, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!followupType) {
      alert('Please select a followup type');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave({
        idEmployee,
        date,
        followup_type: followupType,
        notes: notes.trim() || undefined,
      });
      onClose();
    } catch (error) {
      console.error('Error saving followup:', error);
      alert('Failed to save followup. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Tindak Lanjut</h2>
              <p className="text-sm text-gray-600 mt-1">{employeeName}</p>
              <p className="text-xs text-gray-500">
                {new Date(date).toLocaleDateString('id-ID', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isSubmitting}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jenis Tindak Lanjut <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {FOLLOWUP_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFollowupType(type.value)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      followupType === type.value
                        ? `${type.color} ring-2 ring-offset-1 ring-blue-500`
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Catatan
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Tambahkan catatan tambahan (opsional)..."
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !followupType}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
